import {useCallback, useEffect, useMemo, useState} from "react";
import {
    createLhvPayoutBatch,
    downloadLhvPayoutBatch,
    getCompletedPayoutsPage,
    getPayoutExportBatches,
    getPendingPayouts,
} from "../../services/firestoreService";
import {MAX_PAYOUT_EXPORT_SIZE} from "../../lib/payoutExport";
import {useNavigate} from "react-router-dom";

const COMPLETED_PAGE_SIZE = 10;
const EXPORT_HISTORY_SIZE = 20;

const formatPayoutDate = (value) => {
    if (!value) return "Date unavailable";

    const date = typeof value.toDate === "function"
        ? value.toDate()
        : value.seconds
            ? new Date(value.seconds * 1000)
            : new Date(value);

    return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleString();
};

const createExportRequestId = () => {
    if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
        return window.crypto.randomUUID();
    }
    return `export-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const downloadXmlFile = (xml, fileName) => {
    const blob = new Blob([xml], {type: "application/xml;charset=utf-8"});
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
};

function AdminPayoutManagement() {
    const [activeView, setActiveView] = useState("pending");
    const [pendingPayouts, setPendingPayouts] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [pendingError, setPendingError] = useState("");
    const [selectedTipIDs, setSelectedTipIDs] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [exportRequestId, setExportRequestId] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");

    const [completedPayouts, setCompletedPayouts] = useState([]);
    const [completedLoading, setCompletedLoading] = useState(false);
    const [completedError, setCompletedError] = useState("");
    const [completedLoaded, setCompletedLoaded] = useState(false);
    const [completedPage, setCompletedPage] = useState(1);
    const [completedHasMore, setCompletedHasMore] = useState(false);
    const [completedNextCursor, setCompletedNextCursor] = useState(null);
    const [completedPageCursors, setCompletedPageCursors] = useState([null]);
    const [failedPageRequest, setFailedPageRequest] = useState(null);

    const [exportBatches, setExportBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);
    const [batchesLoaded, setBatchesLoaded] = useState(false);
    const [batchesError, setBatchesError] = useState("");
    const [downloadingBatchID, setDownloadingBatchID] = useState(null);

    const navigate = useNavigate();

    const loadPendingPayouts = useCallback(async () => {
        setPendingLoading(true);
        setPendingError("");

        try {
            const pending = await getPendingPayouts();
            const nextPending = Array.isArray(pending) ? pending : [];
            const pendingIDs = new Set(nextPending.map((payout) => payout.id));
            setPendingPayouts(nextPending);
            setSelectedTipIDs((current) => current.filter((tipID) => pendingIDs.has(tipID)));
        } catch (error) {
            console.error("Failed to load pending payouts:", error);
            setPendingError("Unable to load pending payouts. Please try again.");
        } finally {
            setPendingLoading(false);
        }
    }, []);

    const loadCompletedPage = useCallback(async (cursor, targetPage) => {
        setCompletedLoading(true);
        setCompletedError("");

        try {
            const result = await getCompletedPayoutsPage({
                cursor,
                pageSize: COMPLETED_PAGE_SIZE,
            });

            setCompletedPayouts(Array.isArray(result?.payouts) ? result.payouts : []);
            setCompletedHasMore(Boolean(result?.hasMore));
            setCompletedNextCursor(result?.nextCursor || null);
            setCompletedPage(targetPage);
            setCompletedLoaded(true);
            setFailedPageRequest(null);
        } catch (error) {
            console.error("Failed to load completed payouts:", error);
            setCompletedError("Unable to load completed payouts. Please try again.");
            setFailedPageRequest({cursor, targetPage});
        } finally {
            setCompletedLoading(false);
        }
    }, []);

    const loadExportBatches = useCallback(async () => {
        setBatchesLoading(true);
        setBatchesError("");
        try {
            const batches = await getPayoutExportBatches({pageSize: EXPORT_HISTORY_SIZE});
            setExportBatches(Array.isArray(batches) ? batches : []);
            setBatchesLoaded(true);
        } catch (error) {
            console.error("Failed to load payout export history:", error);
            setBatchesError("Unable to load export history. Please try again.");
        } finally {
            setBatchesLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPendingPayouts();
    }, [loadPendingPayouts]);

    const selectedPayouts = useMemo(() => {
        const selectedIDs = new Set(selectedTipIDs);
        return pendingPayouts.filter((payout) => selectedIDs.has(payout.id));
    }, [pendingPayouts, selectedTipIDs]);

    const selectedTotalCents = useMemo(
        () => selectedPayouts.reduce((sum, payout) => sum + Math.round(Number(payout.amount || 0) * 100), 0),
        [selectedPayouts]
    );

    const showCompletedPayouts = () => {
        setActiveView("completed");
        if (!completedLoaded && !completedLoading) {
            loadCompletedPage(null, 1);
        }
    };

    const showPendingPayouts = () => setActiveView("pending");

    const showExportHistory = () => {
        setActiveView("exports");
        if (!batchesLoaded && !batchesLoading) loadExportBatches();
    };

    const showNextCompletedPage = () => {
        if (completedLoading || !completedHasMore || !completedNextCursor) return;

        const targetPage = completedPage + 1;
        setCompletedPageCursors((currentCursors) => {
            const nextCursors = currentCursors.slice(0, completedPage);
            nextCursors[completedPage] = completedNextCursor;
            return nextCursors;
        });
        loadCompletedPage(completedNextCursor, targetPage);
    };

    const showPreviousCompletedPage = () => {
        if (completedLoading || completedPage <= 1) return;

        const targetPage = completedPage - 1;
        const previousCursor = completedPageCursors[targetPage - 1] || null;
        loadCompletedPage(previousCursor, targetPage);
    };

    const retryCompletedPage = () => {
        const request = failedPageRequest || {
            cursor: completedPageCursors[completedPage - 1] || null,
            targetPage: completedPage,
        };
        loadCompletedPage(request.cursor, request.targetPage);
    };

    const togglePayoutSelection = (payout) => {
        if (!payout.eligible || exporting) return;
        setSuccessMessage("");
        setSelectedTipIDs((current) => {
            if (current.includes(payout.id)) return current.filter((tipID) => tipID !== payout.id);
            if (current.length >= MAX_PAYOUT_EXPORT_SIZE) return current;
            return [...current, payout.id];
        });
    };

    const openExportConfirmation = () => {
        if (selectedPayouts.length === 0 || selectedPayouts.length > MAX_PAYOUT_EXPORT_SIZE) return;
        setExportError("");
        setExportRequestId((current) => current || createExportRequestId());
        setExportModalOpen(true);
    };

    const handleExport = async () => {
        if (!exportRequestId || exporting || selectedPayouts.length === 0) return;
        setExporting(true);
        setExportError("");

        try {
            const result = await createLhvPayoutBatch({
                requestId: exportRequestId,
                tipIDs: selectedPayouts.map((payout) => payout.id),
            });
            downloadXmlFile(result.xml, result.fileName);
            setSuccessMessage(
                `Batch ${result.batchID} exported: ${result.numberOfTransactions} payouts, €${(result.controlSumCents / 100).toFixed(2)}.`
            );
            setExportModalOpen(false);
            setSelectedTipIDs([]);
            setExportRequestId(null);
            setCompletedLoaded(false);
            await Promise.allSettled([loadPendingPayouts(), loadExportBatches()]);
        } catch (error) {
            console.error("Failed to export LHV payout batch:", error);
            setExportError(error.message || "Unable to export the selected payouts.");
        } finally {
            setExporting(false);
        }
    };

    const handleBatchDownload = async (batchID) => {
        if (downloadingBatchID) return;
        setDownloadingBatchID(batchID);
        setBatchesError("");
        try {
            const result = await downloadLhvPayoutBatch(batchID);
            downloadXmlFile(result.xml, result.fileName);
        } catch (error) {
            console.error("Failed to download LHV payout batch:", error);
            setBatchesError(error.message || "Unable to download this payout batch.");
        } finally {
            setDownloadingBatchID(null);
        }
    };

    return (
        <div className="flex flex-col items-center justify-start min-h-screen bg-gray-100 p-6 pt-20 overflow-auto">
            <h1 className="text-3xl font-bold mb-6">Payout Management</h1>

            {successMessage && (
                <div role="status" className="w-full max-w-5xl mb-5 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                    {successMessage}
                </div>
            )}

            <div className="flex flex-wrap w-full max-w-5xl gap-3 mb-6" aria-label="Payout views">
                <button
                    type="button"
                    aria-pressed={activeView === "pending"}
                    className={`px-4 py-2 rounded-md font-semibold ${activeView === "pending" ? "bg-blue-600 text-white" : "bg-white text-gray-700 border border-gray-300"}`}
                    onClick={showPendingPayouts}
                >
                    Pending Payouts
                </button>
                <button
                    type="button"
                    aria-pressed={activeView === "completed"}
                    className={`px-4 py-2 rounded-md font-semibold ${activeView === "completed" ? "bg-blue-600 text-white" : "bg-white text-gray-700 border border-gray-300"}`}
                    onClick={showCompletedPayouts}
                >
                    Completed Payouts
                </button>
                <button
                    type="button"
                    aria-pressed={activeView === "exports"}
                    className={`px-4 py-2 rounded-md font-semibold ${activeView === "exports" ? "bg-blue-600 text-white" : "bg-white text-gray-700 border border-gray-300"}`}
                    onClick={showExportHistory}
                >
                    Export History
                </button>
            </div>

            {activeView === "pending" && (
                <section className="w-full max-w-5xl mb-6" aria-labelledby="pending-payouts-heading">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                        <div>
                            <h2 id="pending-payouts-heading" className="text-2xl font-semibold">Pending Payouts</h2>
                            <p className="mt-1 text-sm text-amber-800">
                                Pending does not guarantee that Stripe funds have settled. Select only payouts confirmed ready.
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-600">
                                Selected: {selectedPayouts.length} / {MAX_PAYOUT_EXPORT_SIZE} · €{(selectedTotalCents / 100).toFixed(2)}
                            </p>
                            <button
                                type="button"
                                className="mt-2 px-4 py-2 bg-green-700 text-white rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={openExportConfirmation}
                                disabled={selectedPayouts.length === 0 || selectedPayouts.length > MAX_PAYOUT_EXPORT_SIZE || exporting}
                            >
                                Export selected to LHV XML
                            </button>
                        </div>
                    </div>

                    {pendingLoading && <p className="text-lg text-gray-500">Loading pending payouts...</p>}

                    {!pendingLoading && pendingError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-red-700">{pendingError}</p>
                            <button type="button" className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md" onClick={loadPendingPayouts}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!pendingLoading && !pendingError && pendingPayouts.length === 0 && (
                        <p className="text-lg text-gray-600">No pending payouts 🎉</p>
                    )}

                    {!pendingLoading && !pendingError && pendingPayouts.map((payout) => {
                        const selected = selectedTipIDs.includes(payout.id);
                        return (
                            <article key={payout.id} className={`bg-white p-4 shadow-md rounded-lg mb-4 border ${selected ? "border-blue-500" : "border-transparent"}`}>
                                <div className="flex items-start gap-4">
                                    <input
                                        type="checkbox"
                                        className="mt-1 h-5 w-5"
                                        aria-label={`Select payout for tip ${payout.id}`}
                                        checked={selected}
                                        disabled={!payout.eligible || exporting || (!selected && selectedTipIDs.length >= MAX_PAYOUT_EXPORT_SIZE)}
                                        onChange={() => togglePayoutSelection(payout)}
                                    />
                                    <div className="flex-1 grid gap-3 md:grid-cols-2">
                                        <div>
                                            <p className="text-lg font-semibold">{payout.recipientName || "Recipient unavailable"}</p>
                                            <p className="text-gray-700">IBAN: {payout.iban || "Unavailable"}</p>
                                            <p className="text-sm text-gray-500">Employee ID: {payout.employeeID || "Unavailable"}</p>
                                            <p className="text-sm text-gray-500">Business: {payout.businessID || "Unavailable"}</p>
                                        </div>
                                        <div className="md:text-right">
                                            <p className="text-gray-800 text-xl font-bold">€{Number(payout.amount || 0).toFixed(2)}</p>
                                            <p className="text-sm text-gray-500">{formatPayoutDate(payout.createdAt)}</p>
                                            <p className="text-xs text-gray-500 mt-1">Tip ID: {payout.id}</p>
                                            <button
                                                type="button"
                                                className="mt-2 text-blue-600 underline"
                                                onClick={() => navigate(`/admin/business/${payout.businessID}/employee/${payout.employeeID}`)}
                                            >
                                                View employee details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {payout.validationError && (
                                    <p role="alert" className="mt-3 ml-9 text-sm font-semibold text-red-700">Cannot export: {payout.validationError}</p>
                                )}
                            </article>
                        );
                    })}
                </section>
            )}

            {activeView === "completed" && (
                <section className="w-full max-w-5xl" aria-labelledby="completed-payouts-heading">
                    <h2 id="completed-payouts-heading" className="text-2xl font-semibold mb-4">Completed Payouts</h2>

                    {completedLoading && <p className="text-lg text-gray-500">Loading completed payouts...</p>}

                    {!completedLoading && completedError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-red-700">{completedError}</p>
                            <button type="button" className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md" onClick={retryCompletedPage}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!completedLoading && !completedError && completedLoaded && completedPayouts.length === 0 && (
                        <p className="text-lg text-gray-600">No completed payouts yet</p>
                    )}

                    {!completedLoading && !completedError && completedPayouts.map((payout) => (
                        <button
                            type="button"
                            key={payout.id}
                            className="w-full bg-gray-200 p-4 shadow-md rounded-lg mb-4 text-left hover:bg-gray-300"
                            onClick={() => navigate(`/admin/payouts/${payout.id}`)}
                        >
                            <p className="text-lg font-semibold">Employee ID: {payout.employeeID}</p>
                            <p className="text-gray-600">Business: {payout.businessID}</p>
                            <p className="text-gray-800 text-xl font-bold">Total Paid: €{Number(payout.totalAmount || 0).toFixed(2)}</p>
                            <p className="text-sm text-gray-500 mt-1">Paid on: {formatPayoutDate(payout.createdAt)}</p>
                            {payout.exportBatchID && <p className="text-xs text-gray-500 mt-1">Export batch: {payout.exportBatchID}</p>}
                        </button>
                    ))}

                    {!completedError && completedLoaded && completedPayouts.length > 0 && (
                        <nav className="flex items-center justify-between mt-6" aria-label="Completed payout pages">
                            <button type="button" className="px-4 py-2 bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed" onClick={showPreviousCompletedPage} disabled={completedPage === 1 || completedLoading}>
                                Previous
                            </button>
                            <span className="text-gray-700">Page {completedPage}</span>
                            <button type="button" className="px-4 py-2 bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed" onClick={showNextCompletedPage} disabled={!completedHasMore || completedLoading}>
                                Next
                            </button>
                        </nav>
                    )}
                </section>
            )}

            {activeView === "exports" && (
                <section className="w-full max-w-5xl" aria-labelledby="export-history-heading">
                    <div className="flex items-center justify-between gap-4 mb-4">
                        <h2 id="export-history-heading" className="text-2xl font-semibold">Export History</h2>
                        <button type="button" className="px-3 py-2 bg-white border border-gray-300 rounded-md" onClick={loadExportBatches} disabled={batchesLoading}>
                            Refresh
                        </button>
                    </div>

                    {batchesLoading && <p className="text-lg text-gray-500">Loading export history...</p>}
                    {!batchesLoading && batchesError && <p role="alert" className="mb-4 text-red-700">{batchesError}</p>}
                    {!batchesLoading && !batchesError && batchesLoaded && exportBatches.length === 0 && (
                        <p className="text-lg text-gray-600">No payout batches exported yet</p>
                    )}

                    {!batchesLoading && exportBatches.map((batch) => (
                        <article key={batch.id} className="bg-white p-4 shadow-md rounded-lg mb-4">
                            <div className="flex flex-wrap justify-between gap-4">
                                <div>
                                    <p className="font-semibold">Batch ID: {batch.id}</p>
                                    <p className="text-sm text-gray-600">Exported: {formatPayoutDate(batch.exportedAt)}</p>
                                    <p className="text-sm text-gray-600">Created by: {batch.createdBy || "Unavailable"}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-semibold">{Number(batch.numberOfTransactions || 0)} payouts</p>
                                    <p className="text-xl font-bold">€{(Number(batch.controlSumCents || 0) / 100).toFixed(2)}</p>
                                    <button
                                        type="button"
                                        className="mt-2 px-3 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                                        onClick={() => handleBatchDownload(batch.id)}
                                        disabled={Boolean(downloadingBatchID)}
                                    >
                                        {downloadingBatchID === batch.id ? "Downloading..." : "Download XML again"}
                                    </button>
                                </div>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 break-all">Payout IDs: {(batch.payoutIDs || []).join(", ") || "Unavailable"}</p>
                        </article>
                    ))}
                </section>
            )}

            {exportModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" role="dialog" aria-modal="true" aria-labelledby="export-confirmation-title">
                    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <h2 id="export-confirmation-title" className="text-xl font-semibold">Confirm LHV XML export</h2>
                        <p className="mt-4">Payouts: <strong>{selectedPayouts.length}</strong></p>
                        <p>Total: <strong>€{(selectedTotalCents / 100).toFixed(2)}</strong></p>
                        <p className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                            Exporting creates one paid payout per selected tip and removes them from Pending. Confirm that sufficient funds are available before continuing.
                        </p>
                        {exportError && <p role="alert" className="mt-3 text-sm text-red-700">{exportError}</p>}
                        <div className="mt-6 flex justify-end gap-3">
                            <button type="button" className="px-4 py-2 bg-gray-200 rounded-md" onClick={() => setExportModalOpen(false)} disabled={exporting}>
                                Cancel
                            </button>
                            <button type="button" className="px-4 py-2 bg-green-700 text-white rounded-md disabled:opacity-50" onClick={handleExport} disabled={exporting}>
                                {exporting ? "Exporting..." : "Export and mark paid"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPayoutManagement;
